"use client";

import { FormEvent, useEffect, useState } from "react";
import { X } from "lucide-react";
import { postEnquiry } from "@/lib/db/property";
import { setUnlocked } from "@/lib/v2/unlock";
import { reportLead } from "@/lib/metaPixel";
import type { UnitInterest } from "@/interfaces/propertyinterface";
import { Button } from "./Button";

type Errors = Partial<Record<"name" | "mobile" | "email" | "consent" | "submit", string>>;

const fieldClass =
  "mt-1.5 h-11 w-full rounded-xl border border-line bg-white px-3 text-sm text-ink outline-none transition focus:border-brand-500 focus:ring-2 focus:ring-brand-100";

function singaporeMobile(value: string): string | null {
  const compact = value.replace(/[\s()-]/g, "");
  const local = compact.replace(/^\+?65/, "");
  return /^\d{8}$/.test(local) ? `+65${local}` : null;
}

export function UnlockModal({
  open,
  onClose,
  propertyId,
  propertyName,
  onUnlocked,
  heading,
  body,
  submitLabel,
  formName = "Price list unlock",
  unitInterest,
}: {
  open: boolean;
  onClose: () => void;
  propertyId: string;
  propertyName: string;
  onUnlocked: () => void;
  heading?: string;
  body?: string;
  submitLabel?: string;
  formName?: string;
  unitInterest?: UnitInterest;
}) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !sending) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, open, sending]);

  if (!open) return null;

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const nextErrors: Errors = {};
    const normalizedMobile = singaporeMobile(mobile);
    if (!name.trim()) nextErrors.name = "Enter your name.";
    if (!normalizedMobile) nextErrors.mobile = "Enter a valid Singapore mobile number.";
    if (email.trim() && !/^\S+@\S+\.\S+$/.test(email.trim())) {
      nextErrors.email = "Enter a valid email address.";
    }
    if (!consent) nextErrors.consent = "Please agree before continuing.";
    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setSending(true);
    setErrors({});
    const result = await postEnquiry({
      name: name.trim(),
      email: email.trim() || undefined,
      mobile_number: normalizedMobile as string,
      brochure: false,
      price_list: true,
      flat_type_id: [],
      property: propertyId,
      source_url: window.location.href,
      unit_interest: unitInterest,
    });
    setSending(false);

    if (typeof result === "string") {
      setErrors({ submit: result });
      return;
    }
    reportLead(formName);

    setUnlocked(propertyId, {
      name: name.trim(),
      mobile: normalizedMobile as string,
      email: email.trim() || undefined,
    });
    onUnlocked();
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/50 p-0 sm:items-center sm:p-4"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !sending) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="unlock-title"
        className="relative max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-white p-6 shadow-2xl sm:max-w-md sm:rounded-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          disabled={sending}
          aria-label="Close"
          className="absolute right-4 top-4 rounded-full p-2 text-ink-muted hover:bg-surface"
        >
          <X aria-hidden="true" className="size-5" />
        </button>
        <h2 id="unlock-title" className="pr-10 font-display text-2xl font-semibold tracking-tight text-ink">
          {heading ?? `See every price for ${propertyName}`}
        </h2>
        <p className="mt-2 text-sm leading-6 text-ink-muted">
          {body ?? "Enter your details once and every unit price and PSF on this page opens up."}
        </p>

        <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
          <Field label="Name" error={errors.name}>
            <input value={name} onChange={(event) => setName(event.target.value)} className={fieldClass} autoComplete="name" />
          </Field>
          <Field label="Mobile" error={errors.mobile}>
            <input value={mobile} onChange={(event) => setMobile(event.target.value)} className={fieldClass} inputMode="tel" autoComplete="tel" placeholder="+65 8877 2688" />
          </Field>
          <Field label="Email (optional)" error={errors.email}>
            <input value={email} onChange={(event) => setEmail(event.target.value)} className={fieldClass} type="email" autoComplete="email" />
          </Field>
          <label className="flex cursor-pointer items-start gap-3 text-sm leading-5 text-ink-muted">
            <input
              type="checkbox"
              checked={consent}
              onChange={(event) => setConsent(event.target.checked)}
              className="mt-0.5 size-4 rounded border-line text-brand-500 focus:ring-brand-500"
            />
            <span>I agree to be contacted about this project. My details are kept private.</span>
          </label>
          {errors.consent && <p className="text-xs text-sold">{errors.consent}</p>}
          {errors.submit && <p className="text-sm text-sold">{errors.submit}</p>}
          <Button type="submit" fullWidth disabled={sending}>
            {sending ? "Sending..." : submitLabel ?? "Show me the prices"}
          </Button>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block text-sm font-medium text-ink">
      {label}
      {children}
      {error && <span className="mt-1 block text-xs font-normal text-sold">{error}</span>}
    </label>
  );
}
